import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDays, Sparkles } from 'lucide-react';
import { ActivityShell, ActivityCrumb } from '../components/ActivityShell';
import { campaignWindowText, fetchCampaign, type CampaignDetail } from '../api';
import { writePendingCampaignKey } from '../../../hooks/useCampaignCapture';
import { applyPageMeta } from '../../../seo/meta';
import { useSiteConfig } from '../../../hooks/useSiteConfig';

/**
 * 活动落地页 `/activity/:key`（公开，免登录）。
 *
 * 一个活动一行数据同时给出页面与规则：页面只展示服务端下发的字段，「送多少」的中文句子
 * 也是服务端算好的（benefitText），三端不许各自拼一遍——拼法一漂就对不上账。
 *
 * 两件事在这里做，别处不做：
 * • 归因：读到哪条活动就把它的标识暂存下来，注册提交时随请求体带走（?campaign= 由全局捕获负责，
 *   两条来源指向同一个键）；
 * • 收录：标题、描述、规范地址与 robots 全部现写，「不在列表里」这一条由后台的 noindex 勾决定，
 *   而不是由前端猜。取不到活动（未上线/已结束）时一律 noindex——这条 URL 没有内容，不该进索引。
 */
export default function ActivityDetailPage() {
  const { key = '' } = useParams();
  const { siteName } = useSiteConfig();
  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchCampaign(key);
      setCampaign(detail);
      writePendingCampaignKey(detail.key);
    } catch (e) {
      setCampaign(null);
      setError(e instanceof Error ? e.message : '活动信息加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const origin = window.location.origin;
    if (!campaign) {
      // 没有内容可展示：连同标题一起回落到「不收录」，别把上一条路由或站点默认标题留在页面上
      const fallbackTitle = `活动详情 - ${siteName}`;
      applyPageMeta({
        title: fallbackTitle,
        description: error ?? '活动信息读取失败，请返回活动列表查看进行中的活动。',
        robots: 'noindex,nofollow',
        canonical: `${origin}/activity`,
        siteName,
      });
      document.title = fallbackTitle;
      return;
    }
    const title = `${campaign.title} - ${siteName}`;
    applyPageMeta({
      title,
      description: campaign.subtitle ?? campaign.benefitText ?? `${campaign.title}活动详情`,
      robots: campaign.noindex ? 'noindex,nofollow' : 'index,follow',
      canonical: `${origin}/activity/${campaign.key}`,
      image: campaign.heroImage,
      siteName,
    });
    document.title = title;
  }, [campaign, error, siteName]);

  if (loading) {
    return (
      <ActivityShell>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <p className="text-sm text-slate-500 py-16 text-center">正在读取活动…</p>
        </div>
      </ActivityShell>
    );
  }

  if (!campaign) {
    return (
      <ActivityShell>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <ActivityCrumb current="活动详情" />
          <div className="py-16 text-center">
            <p className="text-sm text-slate-600 mb-2">{error ?? '活动不存在或已下线'}</p>
            <p className="text-xs text-slate-500 mb-6">
              活动结束或名额发完即从线上撤下，已发放的会员时长不受影响。
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => void load()}
                className="text-sm font-medium px-5 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
              >
                重新加载
              </button>
              <a
                href="/activity"
                className="text-sm font-medium px-5 py-2 rounded-full bg-teal-600 text-white hover:bg-teal-700 transition-colors"
              >
                查看进行中的活动
              </a>
            </div>
          </div>
        </div>
      </ActivityShell>
    );
  }

  // 正文按空行分段（存的是纯文本，页面上不解析 Markdown、也不注入 HTML）
  const paragraphs = (campaign.bodyMd ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <ActivityShell>
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <ActivityCrumb current={campaign.title} />

        {campaign.heroImage && (
          <img
            src={campaign.heroImage}
            alt={campaign.title}
            className="w-full max-h-80 object-cover rounded-2xl mb-8 bg-slate-100"
          />
        )}

        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">{campaign.title}</h1>
        {campaign.subtitle && (
          <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">{campaign.subtitle}</p>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-10">
          {campaign.benefitText && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-teal-50 text-teal-700">
              <Sparkles className="h-4 w-4" />
              {campaign.benefitText}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <CalendarDays className="h-3.5 w-3.5" />
            {campaignWindowText(campaign)}
          </span>
        </div>

        {paragraphs.length > 0 && (
          <div className="space-y-4 mb-10">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-sm text-slate-700 leading-7 whitespace-pre-line">
                {p}
              </p>
            ))}
          </div>
        )}

        <div className="bg-white border border-slate-100 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            参与方式：注册成功后由平台按本页说明发放，无需另外领取。
            {campaign.benefitText ? '' : ' 本活动为说明页，不发放会员时长。'}
          </p>
          <a
            href="/register"
            className="text-sm font-medium px-6 py-2.5 rounded-full bg-teal-600 text-white hover:bg-teal-700 transition-colors text-center whitespace-nowrap"
          >
            {campaign.benefitText ? '注册领取' : '注册账号'}
          </a>
        </div>
      </div>
    </ActivityShell>
  );
}
